import React, { useState, useMemo } from 'react';
import { TrendingDown, AlertCircle, CheckCircle, Search, ChevronDown, ChevronRight, Filter, Award } from 'lucide-react';

// Types
interface VendorQuote {
    vendor_name: string;
    unit_price: number;
    total_price: number;
    quote_date?: string;
    payment_terms?: string;
    matches?: BackendMatch[];
    vsBenchmark?: number | null;
    vsBenchmarkPercent?: number | null;
}
interface BackendMatch {
    reference_description: string;
    reference_unit_price: number;
    similarity_score: number;
    reference_category?: string;
}
interface BackendEntry {
    HEAD: string;
    'SUB HEAD': string;
    DETAILS: string;
    SIZE: string;
    QTY: string;
    'FINAL COST (₹)': string | number;
    VENDOR_NAME?: string;
    vendor_name?: string;
    unit_price: number;
    matches?: BackendMatch[];
    match_count?: number;
    quote_date?: string;
    payment_terms?: string;
    [key: string]: any;
}
interface GroupedDashboardItem {
    HEAD: string;
    SUB_HEAD: string;
    DETAILS: string;
    SIZE: string;
    QTY: string;
    items: BackendEntry[];
    matches: BackendMatch[];
    match_count: number;
    vendors: VendorQuote[];
    bestVendor?: VendorQuote;
    worstVendor?: VendorQuote;
    bestMatch?: BackendMatch | null;
    matchType: 'exact' | 'near' | 'no-match';
    benchmarkPrice?: number | null;
    vendorComparison: VendorQuote[];
    potentialSavings?: number | null;
    interVendorSavings: number;
    predicted_category?: string; // compatibility
}

function safeParseFloat(val: any): number {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
        // Remove comma and non-numeric except dot
        const n = parseFloat(val.replace(/[^\d.]/g, ''));
        if (!isNaN(n)) return n;
    }
    return 0;
}
function safeQtyParse(qtyRaw: string | number): number {
    if (!qtyRaw) return 1;
    if (typeof qtyRaw === 'number') return qtyRaw;
    if (typeof qtyRaw === 'string') {
        const match = qtyRaw.match(/[\d.]+/);
        return match ? parseFloat(match[0]) : 1;
    }
    return 1;
}
function groupBackendData(result: BackendEntry[] = []): Omit<GroupedDashboardItem, 'vendors' | 'bestVendor' | 'worstVendor' | 'bestMatch' | 'benchmarkPrice' | 'vendorComparison' | 'potentialSavings' | 'interVendorSavings' | 'matchType'>[] {
    const grouped: { [key: string]: any } = {};
    result.forEach(entry => {
        const itemKey = JSON.stringify([
            entry.HEAD,
            entry['SUB HEAD'],
            entry.DETAILS,
            entry.SIZE,
            entry.QTY
        ]);
        if (!grouped[itemKey]) {
            grouped[itemKey] = {
                HEAD: entry.HEAD,
                SUB_HEAD: entry['SUB HEAD'],
                DETAILS: entry.DETAILS,
                SIZE: entry.SIZE,
                QTY: entry.QTY,
                predicted_category: entry.predicted_category,
                items: [],
                matches: entry.matches || [],
                match_count: entry.match_count || 0,
            };
        }
        grouped[itemKey].items.push(entry);
    });
    return Object.values(grouped);
}

const MultiVendorBenchmarkingDashboard = ({ data }: { data: { result: BackendEntry[] } }) => {
    if (!data || !Array.isArray(data.result) || data.result.length === 0) {
        return <div className="p-8 text-center text-red-500">No valid data found for dashboard.</div>;
    }
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [sortBy, setSortBy] = useState('best-deal');

    // Group and process the backend data
    const dashboardData: GroupedDashboardItem[] = useMemo(() => {
        if (!data || !data.result) return [];
        return groupBackendData(data.result).map((group): GroupedDashboardItem => {
            // Build unique vendor quotes by vendor_name
            const vendorMap: { [name: string]: VendorQuote } = {};
            group.items.forEach((item: BackendEntry) => {
                const name = String(item.vendor_name || item.VENDOR_NAME || '').trim();
                if (!name) return;
                const unit = safeParseFloat(item.unit_price);
                const total = safeParseFloat(item['FINAL COST (₹)']) || unit;
                // Prefer first occurrence; or choose lower unit price if duplicates
                if (!vendorMap[name] || unit < vendorMap[name].unit_price) {
                    vendorMap[name] = {
                        vendor_name: name,
                        unit_price: unit,
                        total_price: total,
                        quote_date: item.quote_date || '',
                        payment_terms: item.payment_terms || '',
                        matches: item.matches || [],
                    };
                }
            });
            const vendorComparison: VendorQuote[] = Object.values(vendorMap);

            // Best match logic for benchmarks
            let bestMatch: BackendMatch | null = null;
            let matchType: 'exact' | 'near' | 'no-match' = 'no-match';
            if (group.matches && group.matches.length > 0) {
                const exactMatches = group.matches.filter((m: BackendMatch) => m.similarity_score >= 0.8);
                const nearMatches = group.matches.filter((m: BackendMatch) => m.similarity_score >= 0.6 && m.similarity_score < 0.8);
                if (exactMatches.length > 0) {
                    matchType = 'exact';
                    bestMatch = exactMatches.reduce((best, current) => current.similarity_score > best.similarity_score ? current : best);
                } else if (nearMatches.length > 0) {
                    matchType = 'near';
                    bestMatch = nearMatches.reduce((best, current) => current.similarity_score > best.similarity_score ? current : best);
                }
            }
            const benchmarkPrice = bestMatch ? safeParseFloat(bestMatch.reference_unit_price) : null;

            vendorComparison.forEach((vendor: VendorQuote) => {
                const safeVsBenchmark = (vendor.vsBenchmark === undefined || vendor.vsBenchmark === null)
                    ? (benchmarkPrice !== null && benchmarkPrice > 0 ? vendor.unit_price - benchmarkPrice : 0)
                    : vendor.vsBenchmark;
                vendor.vsBenchmark = safeVsBenchmark;
                vendor.vsBenchmarkPercent = (benchmarkPrice && benchmarkPrice > 0)
                    ? ((safeVsBenchmark / benchmarkPrice) * 100)
                    : null;
            });

            const validVendors = vendorComparison.filter(v => typeof v.unit_price === 'number' && !isNaN(v.unit_price));
            const bestVendor = validVendors.length > 0
                ? validVendors.reduce((best, current) => current.unit_price < best.unit_price ? current : best)
                : undefined;
            const worstVendor = validVendors.length > 0
                ? validVendors.reduce((worst, current) => current.unit_price > worst.unit_price ? current : worst)
                : undefined;
            const potentialSavings = (bestVendor && typeof benchmarkPrice === 'number')
                ? (bestVendor.unit_price - benchmarkPrice)
                : null;
            const interVendorSavings = bestVendor && worstVendor
                ? (worstVendor.unit_price - bestVendor.unit_price)
                : 0;
            return {
                ...group,
                vendors: vendorComparison,
                bestVendor,
                worstVendor,
                bestMatch,
                matchType,
                benchmarkPrice,
                vendorComparison,
                potentialSavings,
                interVendorSavings,
            };
        });
    }, [data]);

    // Collect all vendor names across items for dynamic columns
    const allVendorNames = useMemo(() => {
        const set = new Set<string>();
        dashboardData.forEach(item => {
            item.vendors?.forEach(v => {
                if (v.vendor_name) set.add(String(v.vendor_name));
            });
        });
        return Array.from(set);
    }, [dashboardData]);

    // Calculate summary statistics
    const summary = useMemo(() => {
        const totalItems = dashboardData.length;
        const exactMatches = dashboardData.filter(item => item.matchType === 'exact').length;
        const nearMatches = dashboardData.filter(item => item.matchType === 'near').length;
        const noMatches = dashboardData.filter(item => item.matchType === 'no-match').length;
        // Total savings if choosing best vendor vs benchmark
        const totalVsBenchmarkSavings = dashboardData.reduce((sum, item) => {
            const qty = safeQtyParse(item.QTY);
            const potentialSavings = item.potentialSavings ?? 0;
            if (potentialSavings > 0) {
                return sum + (potentialSavings * qty);
            }
            return sum;
        }, 0);
        // Total savings by choosing best vendor vs worst vendor
        const totalInterVendorSavings = dashboardData.reduce((sum, item) => {
            const qty = safeQtyParse(item.QTY);
            const interVendorSavings = item.interVendorSavings ?? 0;
            return sum + (interVendorSavings * qty);
        }, 0);
        const bestVendorBeatsBenchmark = dashboardData.filter(item =>
            item.potentialSavings !== null && item.potentialSavings !== undefined && item.potentialSavings < 0
        ).length;
        const bestVendorAboveBenchmark = dashboardData.filter(item =>
            item.potentialSavings !== null && item.potentialSavings !== undefined && item.potentialSavings > 0
        ).length;
        // Vendor performance stats
        const vendorStats: { [v: string]: { bestCount: number; worstCount: number; totalQuoted: number } } = {};
        dashboardData.forEach(item => {
            if (item.bestVendor) {
                if (!vendorStats[item.bestVendor.vendor_name]) vendorStats[item.bestVendor.vendor_name] = { bestCount: 0, worstCount: 0, totalQuoted: 0 };
                vendorStats[item.bestVendor.vendor_name].bestCount++;
            }
            if (item.worstVendor) {
                if (!vendorStats[item.worstVendor.vendor_name]) vendorStats[item.worstVendor.vendor_name] = { bestCount: 0, worstCount: 0, totalQuoted: 0 };
                vendorStats[item.worstVendor.vendor_name].worstCount++;
            }
            item.vendors?.forEach(v => {
                if (!vendorStats[v.vendor_name]) vendorStats[v.vendor_name] = { bestCount: 0, worstCount: 0, totalQuoted: 0 };
                vendorStats[v.vendor_name].totalQuoted++;
            });
        });
        return {
            totalItems,
            exactMatches,
            nearMatches,
            noMatches,
            totalVsBenchmarkSavings,
            totalInterVendorSavings,
            bestVendorBeatsBenchmark,
            bestVendorAboveBenchmark,
            vendorStats,
        };
    }, [dashboardData]);

    // Filter and sort data
    const filteredData = useMemo(() => {
        let filtered = dashboardData.filter(item => {
            const matchesSearch = (item.DETAILS || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.predicted_category || '').toLowerCase().includes(searchTerm.toLowerCase());
            const matchesFilter = filterType === 'all' ||
                (filterType === 'exact' && item.matchType === 'exact') ||
                (filterType === 'near' && item.matchType === 'near') ||
                (filterType === 'no-match' && item.matchType === 'no-match') ||
                (filterType === 'best-deal' && item.potentialSavings !== null && item.potentialSavings !== undefined && item.potentialSavings < 0) ||
                (filterType === 'negotiate' && item.potentialSavings !== null && item.potentialSavings !== undefined && item.potentialSavings > 0);
            return matchesSearch && matchesFilter;
        });
        // Sort
        if (sortBy === 'best-deal') {
            filtered.sort((a, b) => (a.interVendorSavings ?? 0) - (b.interVendorSavings ?? 0));
        } else if (sortBy === 'savings') {
            filtered.sort((a, b) => (b.interVendorSavings ?? 0) - (a.interVendorSavings ?? 0));
        }
        return filtered;
    }, [dashboardData, searchTerm, filterType, sortBy]);

    const toggleRow = (index) => {

        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedRows(newExpanded);
    };

    const getMatchBadge = (matchType) => {
        switch (matchType) {
            case 'exact':
                return <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold flex items-center gap-1">
                    <CheckCircle size={12} /> Exact
                </span>;
            case 'near':
                return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-semibold flex items-center gap-1">
                    <AlertCircle size={12} /> Near
                </span>;
            default:
                return <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-semibold flex items-center gap-1">
                    <AlertCircle size={12} /> None
                </span>;
        }
    };

    const formatCurrency = (value: number) => {
        if (isNaN(value) || value === null || value === undefined) return '₹0';
        return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
    };

    // Clean vendor name by removing timestamp suffixes like _20251106_063139_0
    const cleanVendorName = (vendorName: string): string => {
        if (!vendorName) return '';
        // Remove pattern: _YYYYMMDD_HHMMSS_N where:
        // - YYYYMMDD is 8 digits (e.g., 20251106)
        // - HHMMSS is 6 digits (e.g., 063139)
        // - N is 1+ digits (e.g., 0, 1)
        return vendorName.replace(/_20\d{6}_\d{6}_\d+$/, '').trim();
    };

    const getVendorBadge = (vendor: VendorQuote, item: GroupedDashboardItem) => {
        const isBest = item.bestVendor?.vendor_name === vendor.vendor_name;
        const isWorst = item.worstVendor?.vendor_name === vendor.vendor_name;

        if (isBest) {
            return <Award className="text-green-600" size={16} />;
        }
        return null;
    };

    const getVsBenchmarkIndicator = (vsBenchmark: number | null, vsBenchmarkPercent: number | null) => {
        if (vsBenchmark === null || vsBenchmark === undefined) return null;

        const isAbove = vsBenchmark > 0;
        const isSignificant = Math.abs(vsBenchmarkPercent || 0) > 10;

        return (
            <div className={`text-xs ${isAbove ? 'text-red-600' : 'text-green-600'}`}>
                {isAbove ? '↑' : '↓'} {Math.abs(vsBenchmarkPercent || 0).toFixed(0)}%
            </div>
        );
    };

    // Helper function for safe display
    function safeDisplayNumber(val: any, nullValue = '-') {
        const n = safeParseFloat(val);
        return (typeof n === 'number' && !isNaN(n)) ? n : nullValue;
    }

    // In table map and anywhere referencing unit_price, total_price, potentialSavings, interVendorSavings, etc.:
    // Use safeParseFloat(vendorA.unit_price), etc.
    // Guard all usages of item.potentialSavings, item.interVendorSavings, etc.

    // In all locations below where you see errors:
    // - Change all direct arithmetic/display of unboxed values to use safeParseFloat and/or with null check.
    // - Example: formatCurrency(item.potentialSavings) => formatCurrency(safeParseFloat(item.potentialSavings) ?? 0)
    // - Example: {item.potentialSavings > 0 ...} => {(safeParseFloat(item.potentialSavings) ?? 0) > 0 ...}
    // - Example: {formatCurrency(item.interVendorSavings)} => {formatCurrency(safeParseFloat(item.interVendorSavings) ?? 0)}

    // For any math involving QTY, use (safeQtyParse(item.QTY) ?? 1)

    // For anything unresolved, default string to '-' or number to 0. Add optional chaining/?? as needed.

    return (
        <div className="min-h-screen bg-gray-50 p-2">
            <div className="max-w-[1700px] mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Cost Benchmarking Dashboard</h1>
                    <p className="text-gray-600">Compare quotes from multiple vendors against historical benchmarks to optimize procurement</p>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-medium text-gray-600">Total Items</h3>
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Filter className="text-blue-600" size={20} />
                            </div>
                        </div>
                        <p className="text-3xl font-bold text-gray-900">{summary.totalItems}</p>
                        <div className="mt-3 flex gap-2 text-xs">
                            <span className="text-green-600">✓ {summary.exactMatches}</span>
                            <span className="text-yellow-600">~ {summary.nearMatches}</span>
                            <span className="text-red-600">✗ {summary.noMatches}</span>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-medium text-gray-600">Vendor Selection Savings</h3>
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <TrendingDown className="text-green-600" size={20} />
                            </div>
                        </div>
                        <p className="text-3xl font-bold text-green-600">{formatCurrency(summary.totalInterVendorSavings)}</p>
                        <p className="text-xs text-gray-500 mt-2">By choosing best vendor for each item</p>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-medium text-gray-600">Best Vendor Performance</h3>
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <Award className="text-purple-600" size={20} />
                            </div>
                        </div>
                        <div className="space-y-1">
                            {Object.entries(summary.vendorStats).map(([vendor, stats]) => (
                                <div key={vendor} className="flex justify-between text-sm">
                                    <span className="text-gray-700">{cleanVendorName(vendor)}</span>
                                    <span className="font-semibold text-purple-600">{stats.bestCount} wins</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-medium text-gray-600">Benchmark Comparison</h3>
                            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                <span className="text-orange-600 text-xl font-bold">₹</span>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <div>
                                <p className="text-sm text-gray-600">Below Benchmark</p>
                                <p className="text-2xl font-bold text-green-600">{summary.bestVendorBeatsBenchmark}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Above Benchmark</p>
                                <p className="text-2xl font-bold text-red-600">{summary.bestVendorAboveBenchmark}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters and Search */}
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <div className="flex flex-col lg:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search items or categories..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <button
                                onClick={() => setFilterType('all')}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterType === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                All Items
                            </button>
                            <button
                                onClick={() => setFilterType('best-deal')}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterType === 'best-deal' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                Best Deals
                            </button>
                            <button
                                onClick={() => setFilterType('negotiate')}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${filterType === 'negotiate' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                Negotiate
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Data Table */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b-2 border-gray-200">
                                <tr>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase" rowSpan={2}>Item Details</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase" rowSpan={2}>Category</th>
                                    <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase" rowSpan={2}>Qty</th>
                                    <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase" colSpan={allVendorNames.length || 1}>Vendor Quotes (Unit Price)</th>
                                    <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase" rowSpan={2}>Benchmark</th>
                                    <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase" rowSpan={2}>Best vs Benchmark</th>
                                    <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase" rowSpan={2}>Vendor Savings</th>
                                    <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase" rowSpan={2}>Match</th>
                                    <th className="px-4 py-4" rowSpan={2}></th>
                                </tr>
                                <tr>
                                    {allVendorNames.length === 0 ? (
                                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 bg-gray-50">Vendors</th>
                                    ) : (
                                        allVendorNames.map(name => (
                                            <th key={name} className="px-4 py-2 text-center text-xs font-semibold text-gray-600 bg-blue-50">{cleanVendorName(name)}</th>
                                        ))
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredData.map((item, index) => {
                                    const safePotentialSavings = item.potentialSavings ?? 0;
                                    const safeInterVendorSavings = item.interVendorSavings ?? 0;
                                    const safeBenchmarkPrice = item.benchmarkPrice ?? null;

                                    const isExpanded = expandedRows.has(index);
                                    return (
                                        <React.Fragment key={index}>
                                            <tr className="hover:bg-gray-50 transition-colors">
                                                <td className={`px-4 py-4 ${isExpanded ? 'border-l-2 border-t-2 border-black' : ''}`}>
                                                    <div className="flex flex-col">
                                                        <span className="font-medium text-gray-900 text-sm">{item.DETAILS || 'N/A'}</span>
                                                        <span className="text-xs text-gray-500">{item.SIZE !== 'NaN' ? item.SIZE : ''}</span>
                                                    </div>
                                                </td>
                                                <td className={`px-4 py-4 ${isExpanded ? 'border-t-2 border-black' : ''}`}>
                                                    <span className="text-xs text-gray-700">{item.predicted_category || 'N/A'}</span>
                                                </td>
                                                <td className={`px-4 py-4 text-center text-sm text-gray-700 ${isExpanded ? 'border-t-2 border-black' : ''}`}>{safeQtyParse(item.QTY)}</td>

                                                {allVendorNames.map(name => {
                                                    const v = item.vendors?.find(vv => String(vv.vendor_name) === String(name));
                                                    const isBest = item.bestVendor?.vendor_name === v?.vendor_name;
                                                    return (
                                                        <td key={name} className={`px-4 py-4 text-center bg-blue-50/30 ${isExpanded ? 'border-t-2 border-black' : ''}`}>
                                                            {v ? (
                                                                <div className="flex flex-col items-center">
                                                                    <div className="flex items-center gap-1">
                                                                        {getVendorBadge(v, item)}
                                                                        <span className={`font-semibold ${isBest ? 'text-green-700' : 'text-gray-900'}`}>
                                                                            {formatCurrency(safeParseFloat(v.unit_price))}
                                                                        </span>
                                                                    </div>
                                                                    {getVsBenchmarkIndicator(v.vsBenchmark ?? null, v.vsBenchmarkPercent ?? null)}
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-400 text-sm">No quote</span>
                                                            )}
                                                        </td>
                                                    );
                                                })}

                                                {/* Benchmark */}
                                                <td className={`px-4 py-4 text-center ${isExpanded ? 'border-t-2 border-black' : ''}`}>
                                                    {safeBenchmarkPrice !== null && safeBenchmarkPrice !== undefined ? (
                                                        <span className="font-semibold text-blue-600 text-sm">
                                                            {formatCurrency(safeParseFloat(safeBenchmarkPrice))}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400 text-sm">-</span>
                                                    )}
                                                </td>

                                                {/* Best vs Benchmark */}
                                                <td className={`px-4 py-4 text-center ${isExpanded ? 'border-t-2 border-black' : ''}`}>
                                                    {safePotentialSavings !== null && safePotentialSavings !== undefined ? (
                                                        <div className="flex flex-col items-center">
                                                            <span className={`font-semibold text-sm ${safePotentialSavings > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                                {safePotentialSavings > 0 ? '+' : ''}{formatCurrency(Math.abs(safePotentialSavings))}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 text-sm">-</span>
                                                    )}
                                                </td>

                                                {/* Inter-vendor Savings */}
                                                <td className={`px-4 py-4 text-center ${isExpanded ? 'border-t-2 border-black' : ''}`}>
                                                    <span className="font-semibold text-green-600 text-sm">
                                                        {formatCurrency(safeInterVendorSavings)}
                                                    </span>
                                                </td>

                                                {/* Match Status */}
                                                <td className={`px-4 py-4 text-center ${isExpanded ? 'border-t-2 border-black' : ''}`}>
                                                    {getMatchBadge(item.matchType)}
                                                </td>

                                                {/* Expand */}
                                                <td className={`px-4 py-4 text-center ${isExpanded ? 'border-t-2 border-r-2 border-black' : ''}`}>
                                                    <button
                                                        onClick={() => toggleRow(index)}
                                                        className="text-blue-600 hover:text-blue-800 transition-colors"
                                                    >
                                                        {expandedRows.has(index) ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                                    </button>
                                                </td>
                                            </tr>

                                            {/* Expanded Row - Detailed Comparison */}
                                            {expandedRows.has(index) && (
                                                <tr className="bg-gray-50">
                                                    <td colSpan={11} className="px-0 py-0 border-l-2 border-r-2 border-b-2 border-black">
                                                        <div className="px-4 py-6">
                                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                                {/* Vendor Details */}
                                                                <div>
                                                                    <h4 className="font-semibold text-gray-900 mb-3">Vendor Quote Details</h4>
                                                                    <div className="space-y-3">
                                                                        {item.vendors?.map((vendor, vIdx) => {
                                                                            const isBest = item.bestVendor?.vendor_name === vendor.vendor_name;
                                                                            const safeVendorVsBenchmark = vendor.vsBenchmark ?? 0;
                                                                            const safePotentialSavings = item.potentialSavings ?? 0;
                                                                            return (
                                                                                <div key={vIdx} className={`bg-white rounded-lg border-2 p-4 ${isBest ? 'border-green-500' : 'border-gray-200'}`}>
                                                                                    <div className="flex items-center justify-between mb-2">
                                                                                        <div className="flex items-center gap-2">
                                                                                            <span className="font-semibold text-gray-900">{cleanVendorName(vendor.vendor_name)}</span>
                                                                                            {isBest && (
                                                                                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-bold flex items-center gap-1">
                                                                                                    <Award size={12} /> BEST PRICE
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                        <span className="text-xs text-gray-500">{vendor.quote_date}</span>
                                                                                    </div>
                                                                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                                                                        <div>
                                                                                            <p className="text-gray-600">Unit Price</p>
                                                                                            <p className="font-bold text-gray-900">{formatCurrency(safeParseFloat(vendor.unit_price))}</p>
                                                                                        </div>
                                                                                        <div>
                                                                                            <p className="text-gray-600">Total Price</p>
                                                                                            <p className="font-bold text-gray-900">{formatCurrency(safeParseFloat(vendor.total_price))}</p>
                                                                                        </div>
                                                                                        <div>
                                                                                            <p className="text-gray-600">Payment Terms</p>
                                                                                            <p className="text-gray-900">{vendor.payment_terms || 'N/A'}</p>
                                                                                        </div>
                                                                                        <div>
                                                                                            <p className="text-gray-600">vs Benchmark</p>
                                                                                            <p className={`font-bold ${vendor.vsBenchmark !== null && vendor.vsBenchmark !== undefined ? (vendor.vsBenchmark > 0 ? 'text-red-600' : 'text-green-600') : 'text-gray-600'}`}>
                                                                                                {vendor.vsBenchmark !== null && vendor.vsBenchmark !== undefined ? (
                                                                                                    <>
                                                                                                        {vendor.vsBenchmark > 0 ? '+' : ''}{formatCurrency(Math.abs(vendor.vsBenchmark))}
                                                                                                        <span className="text-xs ml-1">({vendor.vsBenchmarkPercent !== null && vendor.vsBenchmarkPercent !== undefined ? vendor.vsBenchmarkPercent.toFixed(1) : 'N/A'}%)</span>
                                                                                                    </>
                                                                                                ) : 'N/A'}
                                                                                            </p>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>

                                                                {/* Benchmark Matches & Recommendation */}
                                                                <div className="space-y-4">
                                                                    <div>
                                                                        <h4 className="font-semibold text-gray-900 mb-3">Historical Benchmark Matches</h4>
                                                                        {item.matches && item.matches.length > 0 ? (
                                                                            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                                                                <table className="w-full">
                                                                                    <thead className="bg-gray-100">
                                                                                        <tr>
                                                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">Description</th>
                                                                                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-600">Unit Price</th>
                                                                                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-600">Match Score</th>
                                                                                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-600">Quality</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody className="divide-y divide-gray-200">
                                                                                        {item.matches.map((match, matchIdx) => (
                                                                                            <tr key={matchIdx} className="hover:bg-gray-50">
                                                                                                <td className="px-3 py-2 text-sm text-gray-700">{match.reference_description}</td>
                                                                                                <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">
                                                                                                    {formatCurrency(safeParseFloat(match.reference_unit_price))}
                                                                                                </td>
                                                                                                <td className="px-3 py-2 text-center">
                                                                                                    <span className="text-sm font-medium text-gray-900">
                                                                                                        {(match.similarity_score * 100).toFixed(0)}%
                                                                                                    </span>
                                                                                                </td>
                                                                                                <td className="px-3 py-2 text-center">
                                                                                                    {match.similarity_score >= 0.8 ? (
                                                                                                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                                                                                                            High Match
                                                                                                        </span>
                                                                                                    ) : match.similarity_score >= 0.6 ? (
                                                                                                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                                                                                                            Moderate Match
                                                                                                        </span>
                                                                                                    ) : (
                                                                                                        <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">
                                                                                                            Low Match
                                                                                                        </span>
                                                                                                    )}
                                                                                                </td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                                                                                <AlertCircle className="mx-auto text-gray-400 mb-2" size={32} />
                                                                                <p className="text-gray-600">No benchmark data available</p>
                                                                                <p className="text-xs text-gray-500 mt-1">This will become a benchmark for future use</p>
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {/* Recommendation */}
                                                                    <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                                                                        <h5 className="font-semibold text-blue-900 mb-2">💡 Recommendation</h5>
                                                                        <p className="text-sm text-blue-800">
                                                                            {item.bestVendor ? (
                                                                                <>
                                                                                    <strong>{cleanVendorName(item.bestVendor.vendor_name)}</strong> offers the best price at{' '}
                                                                                    <strong>{formatCurrency(safeParseFloat(item.bestVendor.unit_price))}</strong>.
                                                                                    {safeInterVendorSavings > 0 && (
                                                                                        <> Save <strong>{formatCurrency(safeInterVendorSavings)}</strong> per unit vs highest quote.</>
                                                                                    )}
                                                                                    {safePotentialSavings !== null && safePotentialSavings > 0 && safeBenchmarkPrice && (
                                                                                        <> However, this is <strong>{formatCurrency(safePotentialSavings)}</strong> ({safePotentialSavings !== null && safeBenchmarkPrice ? Math.round((safePotentialSavings / safeBenchmarkPrice) * 100) : 0}%) above benchmark - negotiate for better rates.</>
                                                                                    )}
                                                                                    {safePotentialSavings !== null && safePotentialSavings <= 0 && (
                                                                                        <> This matches or beats the benchmark price! ✓</>
                                                                                    )}
                                                                                    {!safeBenchmarkPrice && (
                                                                                        <> No benchmark available - consider creating one after this purchase.</>
                                                                                    )}
                                                                                </>
                                                                            ) : (
                                                                                <>No vendor quotes available. Request quotes from vendors to proceed.</>
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {filteredData.length === 0 && (
                        <div className="text-center py-12">
                            <p className="text-gray-500 text-lg">No items found matching your search criteria</p>
                        </div>
                    )}
                </div>

                {/* Legend */}
                <div className="mt-6 bg-white rounded-lg shadow p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Legend</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                            <Award className="text-green-600" size={16} />
                            <span className="text-gray-700">Best vendor price for this item</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-green-600">↓ %</span>
                            <span className="text-gray-700">Below benchmark (good deal)</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-red-600">↑ %</span>
                            <span className="text-gray-700">Above benchmark (negotiate)</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MultiVendorBenchmarkingDashboard;